import { useState } from 'react';
import type { MermaidErrorComponentProps } from 'streamdown';
import {
  AlertTriangleIcon,
  ChevronRightIcon,
  RefreshCcwIcon,
  SparklesIcon,
} from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { useMermaidRepair } from './mermaid-repair-context';

/**
 * Replacement for Streamdown's default Mermaid error surface, wired in via
 * `mermaid={{ errorComponent }}` (see response.tsx).
 *
 * Streamdown otherwise renders the raw parser dump verbatim — e.g.
 * "Parse error on line 58: … Expecting 'AMP', 'COLON' … got 'LINK_ID'" — which
 * reads as a scary red failure to a non-technical user. When the client-side
 * sanitizer can't rescue a malformed diagram, this shows a calm, self-contained
 * card instead: a one-line explanation, the diagram source and raw parser error
 * tucked behind disclosures, and a retry button.
 *
 * Props are Streamdown's own `MermaidErrorComponentProps` (`{ error, chart,
 * retry }`), imported so the signature stays compile-time compatible with the
 * `errorComponent` slot on `MermaidOptions`.
 *
 * The primary action is "Ask Coco to fix it", which sends the source and the
 * parse error back as a follow-up turn (see {@link useMermaidRepair}). Streamdown's
 * own `retry` is kept, demoted to a secondary link: it is
 * `() => setCounter(c => c + 1)`, re-rendering identical source through an
 * identical parser, so it can only ever help a transient render failure — never
 * the malformed syntax this card usually reports. Offering it as the headline
 * action, which is what it was before, just reproduces the error on demand.
 */
export function MermaidErrorCard({
  error,
  chart,
  retry,
}: MermaidErrorComponentProps): JSX.Element {
  const [showCode, setShowCode] = useState(false);
  const [showError, setShowError] = useState(false);
  const [repairSent, setRepairSent] = useState(false);
  const { requestRepair } = useMermaidRepair();

  return (
    <div className="my-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
      <div className="flex items-start gap-2">
        <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">This diagram couldn't be drawn.</p>
          <p className="mt-0.5 text-xs text-amber-800">
            The chart had invalid syntax and couldn't be rendered.
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-3">
            {requestRepair !== undefined && (
              <button
                type="button"
                disabled={repairSent}
                onClick={() => {
                  setRepairSent(true);
                  requestRepair(chart, error);
                }}
                className="flex items-center gap-1 rounded-md border border-amber-300 bg-white/70 px-2 py-1 text-xs font-medium text-amber-900 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                <SparklesIcon className="size-3" />
                {repairSent ? 'Asked Coco to fix it' : 'Ask Coco to fix it'}
              </button>
            )}
            <button
              type="button"
              onClick={retry}
              className="flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-950"
            >
              <RefreshCcwIcon className="size-3" />
              Retry
            </button>
            <Disclosure
              open={showCode}
              onToggle={() => setShowCode((v) => !v)}
              label={showCode ? 'Hide diagram code' : 'Show diagram code'}
            />
            <Disclosure
              open={showError}
              onToggle={() => setShowError((v) => !v)}
              label={showError ? 'Hide error' : 'Show error'}
            />
          </div>

          {showCode && <CodeBlock>{chart}</CodeBlock>}
          {showError && <CodeBlock>{error}</CodeBlock>}
        </div>
      </div>
    </div>
  );
}

function Disclosure({
  open,
  onToggle,
  label,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-950"
    >
      <ChevronRightIcon
        className={cn('size-3 transition-transform', open && 'rotate-90')}
      />
      {label}
    </button>
  );
}

function CodeBlock({ children }: { children: string }): JSX.Element {
  return (
    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded border border-amber-200 bg-white/70 p-2 font-mono text-xs text-amber-950">
      {children}
    </pre>
  );
}
